import { APIURL, appendFlatpickrBaseStyles, initFlatpickrInShadow, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, apiGet, toggleWidgetTheme, updateSelectedSymbolInDropdown, normalizeWidgetTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';
import flatpickr from 'flatpickr';
import { format } from 'date-fns';

export class TraderverseBullvsBearTargetMeter {
  private container: HTMLElement;
  private config: WidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any[] = [];
  private currentDataResponse: any = null;
  private widgetResizeObserver: ResizeObserver | null = null;
  private datePickerInstance: any = null;
  private outsideClickHandler: ((e: MouseEvent) => void) | null = null;
  private startDate: string = '';
  private endDate: string = '';
  // private widgetIndex: number = 0;
  private filters: {
    upsideThreshold: number;
    downsideThreshold: number;
    timeWindow: '24h' | '3d' | '7d' | 'custom';
    customStart?: string;
    customEnd?: string;
    firmMode: 'include' | 'exclude';
    firmFilter: string[];
    showOnlySignificant: boolean;
    showOnlyPercent: number;
  };

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    // default filter state
    this.filters = {
      upsideThreshold: 10,
      downsideThreshold: 5,
      timeWindow: '7d',
      customStart: undefined,
      customEnd: undefined,
      firmMode: 'include',
      firmFilter: [],
      showOnlySignificant: true,
      showOnlyPercent: 5
    };

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }

    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => this.updateDisplay());
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' },
      { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' },
      { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' },
      { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' },
      { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' },
      { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.injectChartLibrary();
    this.attachStyles();
    this.setupEventListeners();
    await this.setupChart(this.config.symbol!);
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
  let dataTheme = 'default-dark';
  
  if (this.config.theme === 'white') {
    themeClass = 'whiteMode';
    dataTheme = 'white';
  } else if (this.config.theme === 'black') {
    themeClass = 'blackMode';
    dataTheme = 'black';
  } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
    const widgetHtml = `
        <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title market">${this.getTitleIconSvg()} Bull vs Bear Target Meter </h4>
                <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<div class="item">
                <button class="filters-toggle" type="button"> <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg> </button>
              </div></div>
          </div>
          <div class="inBody">
            <div class="optionsRow">
              <div class="item dateRange">
                <input type="text" class="date-input" data-testing-id="date-range-input" />
                <i><svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M9.91667 1.16667H8.75V0.583333C8.75 0.428624 8.68854 0.280251 8.57915 0.170854C8.46975 0.0614581 8.32138 0 8.16667 0C8.01196 0 7.86358 0.0614581 7.75419 0.170854C7.64479 0.280251 7.58333 0.428624 7.58333 0.583333V1.16667H4.08333V0.583333C4.08333 0.428624 4.02188 0.280251 3.91248 0.170854C3.80308 0.0614581 3.65471 0 3.5 0C3.34529 0 3.19692 0.0614581 3.08752 0.170854C2.97813 0.280251 2.91667 0.428624 2.91667 0.583333V1.16667H1.75C1.28587 1.16667 0.840752 1.35104 0.512563 1.67923C0.184375 2.00742 0 2.45254 0 2.91667V9.91667C0 10.3808 0.184375 10.8259 0.512563 11.1541C0.840752 11.4823 1.28587 11.6667 1.75 11.6667H9.91667C10.3808 11.6667 10.8259 11.4823 11.1541 11.1541C11.4823 10.8259 11.6667 10.3808 11.6667 9.91667V2.91667C11.6667 2.45254 11.4823 2.00742 11.1541 1.67923C10.8259 1.35104 10.3808 1.16667 9.91667 1.16667ZM10.5 9.91667C10.5 10.0714 10.4385 10.2198 10.3291 10.3291C10.2198 10.4385 10.0714 10.5 9.91667 10.5H1.75C1.59529 10.5 1.44692 10.4385 1.33752 10.3291C1.22812 10.2198 1.16667 10.0714 1.16667 9.91667V5.83333H10.5V9.91667ZM10.5 4.66667H1.16667V2.91667C1.16667 2.76196 1.22812 2.61358 1.33752 2.50419C1.44692 2.39479 1.59529 2.33333 1.75 2.33333H2.91667V2.91667C2.91667 3.07138 2.97813 3.21975 3.08752 3.32915C3.19692 3.43854 3.34529 3.5 3.5 3.5C3.65471 3.5 3.80308 3.43854 3.91248 3.32915C4.02188 3.21975 4.08333 3.07138 4.08333 2.91667V2.33333H7.58333V2.91667C7.58333 3.07138 7.64479 3.21975 7.75419 3.32915C7.86358 3.43854 8.01196 3.5 8.16667 3.5C8.32138 3.5 8.46975 3.43854 8.57915 3.32915C8.68854 3.21975 8.75 3.07138 8.75 2.91667V2.33333H9.91667C10.0714 2.33333 10.2198 2.39479 10.3291 2.50419C10.4385 2.61358 10.5 2.76196 10.5 2.91667V4.66667Z" fill="#9F9FA2"/> </svg> </i>
              </div>
              <span></span>



              <div class="filtersBox sideBar hidden">
                
                <div class="item">
                  <label>Upside Threshold</label>
                  <input id="upsideThresholdInput" type="number" min="0" max="100" step="1" value="10" data-testing-id="upside-threshold-input" />
                </div>
                <div class="item">
                  <label>Downside Threshold</label>
                  <input id="downsideThresholdInput" type="number" min="0" max="100" step="1" value="5" data-testing-id="downside-threshold-input" />
                </div>
                <div class="item">
                  <label>Time</label>
                  <select id="timeWindowSelect" data-testing-id="time-window-select">
                    <option value="24h">Last 24h</option>
                    <option value="3d">3 days</option>
                    <option value="7d" selected>7 days</option>
                  </select>
                </div>
                <div class="item">
                  <label>Mode</label>
                  <div class="fieldsGroup">
                    <select id="firmModeSelect" style="--group-item-width:0 0 90px;" data-testing-id="firm-mode-select">
                      <option value="include" selected>Include</option>
                      <option value="exclude">Exclude</option>
                    </select>
                    <input id="firmFilterInput" type="text" placeholder="Comma-separated firms" data-testing-id="firm-filter-input" />
                  </div>
                </div>
                
                <div class="item customDates" style="display:none;gap:8px;align-items:center;">
                  <input id="customStartInput" type="date" data-testing-id="custom-start-input" />
                  <input id="customEndInput" type="date" data-testing-id="custom-end-input" />
                </div>
              </div>
              
            </div>

            <div class="chartDv bearTargetMeterMain">
              
              <div class="metersRow">
                <div class="meter" data-type="bullish">
                  <div class="arcWrapper">
                    <div class="arcOutter"><div class="arc" id="bullArc" style="--value:16; --color:#34E36F;"></div></div>
                    <div class="needle" id="bullNeedle"></div>
                    <div class="center"><span class="percent" id="bullPercent">16%</span></div>
                  </div>
                  <div class="meterLabel">Bullish</div>
                </div>

                <div class="meter" data-type="bearish">
                  <div class="arcWrapper">
                    <div class="arcOutter"><div class="arc" id="bearArc" style="--value:84; --color:#FF1111;"></div></div>
                    <div class="needle" id="bearNeedle"></div>
                    <div class="center"><span class="percent" id="bearPercent">84%</span></div>
                  </div>
                  <div class="meterLabel">Bearish</div>
                </div>
              </div>

              <div class="statsRow">
                <div class="statBox bullishBox"><div class="label">Bullish</div><div class="value" id="statBull">1</div></div>
                <div class="statBox neutralBox"><div class="label">Neutral</div><div class="value" id="statNeutral">5</div></div>
                <div class="statBox bearishBox"><div class="label">Bearish</div><div class="value" id="statBear">2</div></div>
                <div class="statBox sentimentBox"><div class="label">Sentiment</div><div class="value sentimentText" id="statSentiment">Bearish</div></div>
              </div>

              <div id="noDataMessage" class="noData"><p>No price target data found</p></div>
              
            </div>
          </div>
        </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;

    this.initDatePicker();
  }

  private async attachStyles(): Promise<void> {
    try {
      appendFlatpickrBaseStyles(this.shadowRoot);
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    dropdown?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value;
      this.changeSymbol(value);
    });
    if (dropdown && this.config.symbol) dropdown.value = this.config.symbol;

    const upsideEl = this.shadowRoot.querySelector('#upsideThresholdInput') as HTMLInputElement;
    const downsideEl = this.shadowRoot.querySelector('#downsideThresholdInput') as HTMLInputElement;
    const timeWindowEl = this.shadowRoot.querySelector('#timeWindowSelect') as HTMLSelectElement;
    const customStartEl = this.shadowRoot.querySelector('#customStartInput') as HTMLInputElement;
    const customEndEl = this.shadowRoot.querySelector('#customEndInput') as HTMLInputElement;
    const customDatesWrapper = this.shadowRoot.querySelector('.customDates') as HTMLElement;
    const firmModeEl = this.shadowRoot.querySelector('#firmModeSelect') as HTMLSelectElement;
    const firmFilterEl = this.shadowRoot.querySelector('#firmFilterInput') as HTMLInputElement;
    const showOnlyToggleEl = this.shadowRoot.querySelector('#showOnlyToggle') as HTMLInputElement;
    const showOnlyPercentEl = this.shadowRoot.querySelector('#showOnlyPercentInput') as HTMLInputElement;

    if (upsideEl) {
      upsideEl.value = String(this.filters.upsideThreshold);
      upsideEl.addEventListener('change', () => {
        this.filters.upsideThreshold = Number(upsideEl.value || 0);
        this.setupChart(this.config.symbol!);
      });
    }
    if (downsideEl) {
      downsideEl.value = String(this.filters.downsideThreshold);
      downsideEl.addEventListener('change', () => {
        this.filters.downsideThreshold = Number(downsideEl.value || 0);
        this.setupChart(this.config.symbol!);
      });
    }
    if (timeWindowEl) {
      timeWindowEl.value = this.filters.timeWindow;
      timeWindowEl.addEventListener('change', () => {
        const val = timeWindowEl.value as any;
        this.filters.timeWindow = val;
        if (val === 'custom') {
          if (customDatesWrapper) customDatesWrapper.style.display = 'flex';
        } else {
          if (customDatesWrapper) customDatesWrapper.style.display = 'none';
          this.filters.customStart = undefined;
          this.filters.customEnd = undefined;
        }
        this.setupChart(this.config.symbol!);
      });
    }
    if (customStartEl) {
      customStartEl.value = this.filters.customStart || '';
      customStartEl.addEventListener('change', () => {
        this.filters.customStart = customStartEl.value || undefined;
        this.setupChart(this.config.symbol!);
      });
    }
    if (customEndEl) {
      customEndEl.value = this.filters.customEnd || '';
      customEndEl.addEventListener('change', () => {
        this.filters.customEnd = customEndEl.value || undefined;
        this.setupChart(this.config.symbol!);
      });
    }
    if (firmModeEl) {
      firmModeEl.value = this.filters.firmMode;
      firmModeEl.addEventListener('change', () => {
        this.filters.firmMode = firmModeEl.value as any;
        this.setupChart(this.config.symbol!);
      });
    }
    if (firmFilterEl) {
      firmFilterEl.addEventListener('change', () => {
        const raw = firmFilterEl.value || '';
        this.filters.firmFilter = raw.split(',').map(s => s.trim()).filter(Boolean);
        this.setupChart(this.config.symbol!);
      });
    }
    if (showOnlyToggleEl) {
      showOnlyToggleEl.checked = !!this.filters.showOnlySignificant;
      showOnlyToggleEl.addEventListener('change', () => {
        this.filters.showOnlySignificant = !!showOnlyToggleEl.checked;
        this.setupChart(this.config.symbol!);
      });
    }
    if (showOnlyPercentEl) {
      showOnlyPercentEl.value = String(this.filters.showOnlyPercent);
      showOnlyPercentEl.addEventListener('change', () => {
        this.filters.showOnlyPercent = Number(showOnlyPercentEl.value || 0);
        this.setupChart(this.config.symbol!);
      });
    }

    const filtersBtn = this.shadowRoot.querySelector('.filters-toggle') as HTMLElement | null;
    const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement | null;
    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement | null;

    if (filtersBtn && filtersBox) {
      filtersBtn.addEventListener('click', (ev: MouseEvent) => {
        ev.stopPropagation();
        if (filtersBox.classList.contains('hidden')) {
          filtersBox.classList.remove('hidden');
        } else {
          filtersBox.classList.add('hidden');
        }
      });

      filtersBox.addEventListener('click', (ev: MouseEvent) => ev.stopPropagation());

      this.outsideClickHandler = (ev: MouseEvent) => {
        const anyEv: any = ev;
        const path: EventTarget[] = (anyEv.composedPath && anyEv.composedPath()) || anyEv.path || [];
        const clickedInside = path.indexOf(this.container) !== -1 || (widgetBox && path.indexOf(widgetBox) !== -1);
        if (!clickedInside) {
          if (!filtersBox.classList.contains('hidden')) filtersBox.classList.add('hidden');
        }
      };

      document.addEventListener('click', this.outsideClickHandler);
    }
  }

  onSelectChange(stock: any) {
    this.changeSymbol(stock.symbol, stock);
  }

  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);
      if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
        const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
        script.onload = () => resolve(true);
      } else {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
        script.async = true;
        script.setAttribute('data-chart-lib', 'true');
        this.shadowRoot.appendChild(script);
        script.onload = () => resolve(true);
      }
    });
  }

  private async setupChart(symbol: string): Promise<void> {
    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    try {
      const base = `${APIURL}/v1/discover/bull-bear-target-meter`;
      const params = new URLSearchParams();
      params.append('p_symbol', symbol || (this.config.symbol as string) || '');

      const now = new Date();
      const fmt = (d: Date) => {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      };

      let startDate: string | undefined;
      let endDate: string | undefined = fmt(now);
      if (this.filters.timeWindow === '24h') {
        const past = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        startDate = fmt(past);
      } else if (this.filters.timeWindow === '3d') {
        const past = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000);
        startDate = fmt(past);
      } else if (this.filters.timeWindow === '7d') {
        const past = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        startDate = fmt(past);
      } else if (this.filters.timeWindow === 'custom') {
        if (this.filters.customStart) startDate = this.filters.customStart;
        if (this.filters.customEnd) endDate = this.filters.customEnd;
      }

      if (startDate) params.append('p_start_date', startDate);
      if (endDate) params.append('p_end_date', endDate);
      this.startDate = startDate || '';
      this.endDate = endDate || '';

      params.append('p_upside_threshold', String(this.filters.upsideThreshold));
      params.append('p_downside_threshold', String(this.filters.downsideThreshold));

      if (this.filters.firmFilter && this.filters.firmFilter.length) {
        this.filters.firmFilter.forEach(f => params.append('p_firm_filter', f));
        params.append('p_firm_filter_mode', this.filters.firmMode);
      }

      params.append('p_show_only_significant', String(!!this.filters.showOnlySignificant));
      params.append('p_show_only_significant_threshold', String(this.filters.showOnlyPercent || 0));

      const url = `${base}?${params.toString()}`;

      const res = await apiGet({
        method: 'GET',
        url,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!res.ok) throw new Error(`Failed to fetch data (${res.status})`);

      const json = await res.json();
      this.currentDataResponse = json;
      this.currentData = [];
      
      if (!json.data?.meters && !json.data?.summary) {
        widgetBox?.removeAttribute('data-testing-id');
      }

      this.updateDisplay();
      this.emit('dataUpdate', json);
    } catch (err) {
      console.error('Error fetching chart data:', err);
      widgetBox?.removeAttribute('data-testing-id');
    }
  }

  private updateDisplay(): void {
    const json = this.currentDataResponse;
      const chartDv = this.shadowRoot.querySelector('.chartDv') as HTMLElement | null;
    const noDataEl = this.shadowRoot.querySelector('#noDataMessage') as HTMLElement | null;
    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;

    if (!json || !json.data) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }

    const hasMeters = !!json.data?.meters;
    const hasSummary = !!json.data?.summary;
    const hasData = hasMeters || hasSummary;

    if (chartDv) {
      if (hasData) {
        chartDv.setAttribute('data-testing-id', 'top-index-content');
      } else {
        chartDv.removeAttribute('data-testing-id');
      }
    }

    if (!hasData) {
      if (noDataEl) noDataEl.style.display = 'block';
      widgetBox?.removeAttribute('data-testing-id');
      const metersRow = this.shadowRoot.querySelector('.metersRow') as HTMLElement | null;
      const statsRow = this.shadowRoot.querySelector('.statsRow') as HTMLElement | null;
      if (metersRow) metersRow.style.display = 'none';
      if (statsRow) statsRow.style.display = 'none';
      return;
    }

    if (noDataEl) noDataEl.style.display = 'none';
    const metersRow = this.shadowRoot.querySelector('.metersRow') as HTMLElement | null;
    const statsRow = this.shadowRoot.querySelector('.statsRow') as HTMLElement | null;
    if (metersRow) metersRow.style.display = '';
    if (statsRow) statsRow.style.display = '';

    const meters = json.data?.meters;
    const summary = json.data?.summary;

    const gaugeValue = meters?.bullish_meter?.percentage ?? summary?.bullish_percentage ?? 0;
    const classification = summary?.overall_sentiment ?? (gaugeValue > 50 ? 'Bullish' : 'Bearish');
    this.renderMeters(gaugeValue, classification);
    this.initDatePicker();
  }

  private renderMeters(gaugeValue: number, sentimentText: string): void {
    const bullVal = Math.round(gaugeValue);
    const bearVal = Math.round(100 - gaugeValue);

    const bullArc = this.shadowRoot.querySelector('#bullArc') as HTMLElement;
    const bearArc = this.shadowRoot.querySelector('#bearArc') as HTMLElement;
    const bullNeedle = this.shadowRoot.querySelector('#bullNeedle') as HTMLElement;
    const bearNeedle = this.shadowRoot.querySelector('#bearNeedle') as HTMLElement;
    const bullPercent = this.shadowRoot.querySelector('#bullPercent') as HTMLElement;
    const bearPercent = this.shadowRoot.querySelector('#bearPercent') as HTMLElement;

    const json = this.currentDataResponse;
    const meters = json?.data?.meters;
    const summary = json?.data?.summary;

    const bullColor = '#34E36F';
    const bearColor = '#FF1111';

    if (bullArc) {
      bullArc.style.setProperty('--value', String(bullVal / 2));
      bullArc.style.setProperty('--color', bullColor);
    }
    if (bearArc) {
      bearArc.style.setProperty('--value', String(bearVal / 2));
      bearArc.style.setProperty('--color', bearColor);
    }
    if (bullPercent) {
      bullPercent.textContent = `${bullVal}%`;
      bullPercent.style.color = bullColor;
    }
    if (bearPercent) {
      bearPercent.textContent = `${bearVal}%`;
      bearPercent.style.color = bearColor;
    }

    const bullAngle = (bullVal / 100) * 180 - 90;
    const bearAngle = (bearVal / 100) * 180 - 90;
    if (bullNeedle) bullNeedle.style.transform = `rotate(${bullAngle}deg)`;
    if (bearNeedle) bearNeedle.style.transform = `rotate(${bearAngle}deg)`;

    const statBullEl = this.shadowRoot.querySelector('#statBull') as HTMLElement;
    const statNeutralEl = this.shadowRoot.querySelector('#statNeutral') as HTMLElement;
    const statBearEl = this.shadowRoot.querySelector('#statBear') as HTMLElement;
    const statSentEl = this.shadowRoot.querySelector('#statSentiment') as HTMLElement;

    const bullCount = meters?.bullish_meter?.count ?? summary?.bullish_count ?? 0;
    const neutralCount = meters?.neutral_meter?.count ?? summary?.neutral_count ?? 0;
    const bearCount = meters?.bearish_meter?.count ?? summary?.bearish_count ?? 0;
    const sentiment = summary?.overall_sentiment ?? sentimentText ?? (gaugeValue > 50 ? 'Bullish' : 'Bearish');
    const sentimentColor = summary?.sentiment_color ?? (sentiment === 'Bullish' ? bullColor : bearColor);

    if (statBullEl) statBullEl.textContent = String(bullCount);
    if (statNeutralEl) statNeutralEl.textContent = String(neutralCount);
    if (statBearEl) statBearEl.textContent = String(bearCount);
    if (statSentEl) {
      statSentEl.textContent = sentiment;
      (statSentEl.style as any).color = sentimentColor;
    }
  }

  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(
      this.shadowRoot,
      this.config,
      stock,
      stockId,
      (stockId) => this.setupChart(stockId),
      (type, data) => this.emit(type, data)
    );
  }

  private initDatePicker(): void {
    try {
      const input = this.shadowRoot.querySelector('.date-input') as HTMLInputElement;
      if (!input) return;

      if (this.datePickerInstance && this.datePickerInstance.destroy) {
        this.datePickerInstance.destroy();
        this.datePickerInstance = null;
      }

      const defaultDates = (this.filters.customStart && this.filters.customEnd)
        ? [this.filters.customStart, this.filters.customEnd]
        : (this.startDate && this.endDate) ? [this.startDate, this.endDate] : undefined;

      if (defaultDates && defaultDates.length === 2) {
        input.value = `${defaultDates[0]} to ${defaultDates[1]}`;
      }

      this.datePickerInstance = initFlatpickrInShadow(input, this.shadowRoot, {
        mode: 'range',
        dateFormat: 'Y-m-d',
        defaultDate: defaultDates,
        onClose: (selectedDates: Date[]) => {
          if (selectedDates.length === 2) {
            const [start, end] = selectedDates;
            this.startDate = format(start, 'yyyy-MM-dd');
            this.endDate = format(end, 'yyyy-MM-dd');
            this.filters.timeWindow = 'custom';
            this.filters.customStart = this.startDate;
            this.filters.customEnd = this.endDate;

            const timeWindowEl = this.shadowRoot.querySelector('#timeWindowSelect') as HTMLSelectElement;
            if (timeWindowEl) timeWindowEl.value = 'custom';

            const customStartEl = this.shadowRoot.querySelector('#customStartInput') as HTMLInputElement;
            const customEndEl = this.shadowRoot.querySelector('#customEndInput') as HTMLInputElement;
            if (customStartEl) customStartEl.value = this.startDate;
            if (customEndEl) customEndEl.value = this.endDate;

            input.value = `${this.startDate} to ${this.endDate}`;

            this.setupChart(this.config.symbol!);
          }
        },
      });
    } catch (err) {
      console.warn('Date picker init failed:', err);
    }
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }

  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }

  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    this.updateDisplay();
  }

  public destroy(): void {
    this.widgetResizeObserver?.disconnect();
    if (this.updateInterval) clearInterval(this.updateInterval);
    if (this.outsideClickHandler) {
      document.removeEventListener('click', this.outsideClickHandler);
      this.outsideClickHandler = null;
    }
    this.container.innerHTML = '';
    this.eventHandlers.clear();
  }

  private emit(type: WidgetEventType, data: any): void {
    this.eventHandlers.get(type)?.forEach(handler => handler({ type, data }));
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
  }

}

declare global {
  interface Window { TraderverseBullvsBearTargetMeter: typeof TraderverseBullvsBearTargetMeter; }
}
if (typeof window !== 'undefined') {
  window.TraderverseBullvsBearTargetMeter = TraderverseBullvsBearTargetMeter;
}